import type { ButtonHTMLAttributes, CSSProperties } from "react";

type Variant = "primary" | "success" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

/** Bouton partagé : centralise variantes, rayon et typographie pour la cohérence.
 *  Les états hover/active/focus sont gérés globalement (src/styles/global.css). */
export default function Button({
  variant = "secondary",
  size = "md",
  style,
  ...rest
}: Props) {
  return (
    <button
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      {...rest}
    />
  );
}

const base: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  flexShrink: 0,
  whiteSpace: "nowrap",
  borderRadius: "var(--radius)",
  fontWeight: 600,
  fontFamily: "inherit",
  lineHeight: 1.2,
  border: "1px solid transparent",
};

const sizes: Record<Size, CSSProperties> = {
  sm: { padding: "4px 10px", fontSize: 13 },
  md: { padding: "8px 16px", fontSize: 14 },
};

const variants: Record<Variant, CSSProperties> = {
  // `--accent` est défini par cours (barre de contexte enseignant) ; défaut = bleu.
  primary: { background: "var(--accent, var(--blue))", color: "var(--on-accent)", fontWeight: 700 },
  success: { background: "var(--green)", color: "var(--on-accent)", fontWeight: 700 },
  secondary: {
    background: "transparent",
    color: "var(--text)",
    border: "1px solid var(--border)",
  },
  ghost: { background: "transparent", color: "var(--text-muted)" },
  danger: {
    background: "transparent",
    color: "var(--red)",
    border: "1px solid var(--red)",
  },
};
